import { components } from "@appretio/api";
import type { AlgorithmicForecastLoad } from "./algorithmicForecastLoad";
import { FORECAST_ERROR_COPY } from "./algorithmicForecastOptions";

type AlgorithmicForecastOutput =
  components["schemas"]["AlgorithmicForecastOutput"];

/** A model's outcome: the load minus the ground-truth label the response does not echo. */
export type ModelOutcome = Pick<AlgorithmicForecastLoad, "output" | "failures">;

/**
 * Only `insufficient_history` has a `detail` written for planners: the weeks
 * the model needs and has. `invalid_history` names its first hole as a
 * packed `year_week` int, and `fit_failed` carries the library's exception —
 * both stay in telemetry.
 */
function detailOf(error: components["schemas"]["ForecastError"]): string {
  return error.detail && error.reason === "insufficient_history"
    ? ` (${error.detail})`
    : "";
}

/**
 * True when the response carries rows. Success is rows, not error-free
 * diagnostics: the schema documents a no-rows response with absent
 * diagnostics (a span the calendar cannot fill), which must not read as
 * success.
 */
export function producedRows(output: AlgorithmicForecastOutput): boolean {
  return (output.fiscal_grid_data ?? []).length > 0;
}

/**
 * Pairs each model's response with its planner-facing failure lines. Lines
 * name the model only when more than one was requested — `nameModels` says
 * so when a request failed and fewer outputs than models arrive.
 * `modelLabels` runs
 * parallel to `outputs`.
 */
export function summarizeGenerateOutputs(
  outputs: AlgorithmicForecastOutput[],
  modelLabels: string[],
  labelKpi: (metricName: string) => string,
  nameModels = outputs.length > 1
): ModelOutcome[] {
  return outputs.map((output, index) => {
    const prefix = nameModels ? `${modelLabels[index]} · ` : "";
    const failures = (output.diagnostics ?? []).flatMap((d) =>
      d.error
        ? [
            `${prefix}${labelKpi(d.metric_name)}: ${FORECAST_ERROR_COPY[d.error.reason]}${detailOf(d.error)}`
          ]
        : []
    );
    if (!producedRows(output) && failures.length === 0) {
      failures.push(`${prefix}no data produced for this context`);
    }
    return { output, failures };
  });
}
